/**
 * Game API Factory - Creates game-type-specific API clients
 *
 * Uses inlined game-client module for the game-agnostic room API (v2).
 * Each game type gets its own API instance pre-bound to the gameType string.
 *
 * SECURITY: Authentication is handled via httpOnly cookies.
 * All requests include credentials to send cookies automatically.
 */

import {
  createGamePlatformApiClient,
  type GamePlatformEvent,
  type GamePlatformRoom,
  type ReplayShare,
} from "@cloudnest/board-game-runtime";
import { createAuthFetch } from "../auth/auth-fetch";
import { getCsrfHeaders } from "../auth/csrf";
import type { ApiResponse } from "../auth/types";
import { getConfig } from "../config/runtime-config";
import type {
  GameEvent,
  GameRoom,
  PlayerStats,
  ResolvedShareLink,
  ShareLink,
} from "../game-client";
import { submitPlatformEvent } from "./submit-platform-event";

const authFetch = createAuthFetch(getConfig().multiplayerServiceUrl);
const platformApi = createGamePlatformApiClient({
  baseUrl: getConfig().multiplayerServiceUrl,
  defaultHeaders: getCsrfHeaders,
});

// Re-export shared types for convenience
export type {
  GameEvent,
  GameRoom,
  PlayerStats,
  ReplayShare,
  ResolvedShareLink,
  ShareLink,
};

/**
 * Compatibility type while per-game route-mode helpers are collapsed.
 * createGameApi always returns the game-platform client.
 */
export type GameApiMode = "legacy" | "game-platform";

export interface GameApi {
  readonly getRoom: (roomId: string) => Promise<ApiResponse<GameRoom>>;
  readonly createShareLink: (
    roomId: string,
    ttlMs?: number,
  ) => Promise<ApiResponse<ShareLink>>;
  readonly createObserverShareLink: (
    roomId: string,
    ttlMs?: number,
  ) => Promise<ApiResponse<ShareLink>>;
  readonly resolveShareLink: (
    gameType: string,
    roomNumber: string,
    secret: string,
  ) => Promise<ApiResponse<ResolvedShareLink>>;
  readonly joinByShareLink: (
    gameType: string,
    roomNumber: string,
    secret: string,
  ) => Promise<ApiResponse<GameRoom>>;
  readonly joinObserverByShareLink: (
    gameType: string,
    roomNumber: string,
    secret: string,
  ) => Promise<ApiResponse<GameRoom>>;
  readonly createGame: (
    config?: Record<string, unknown>,
  ) => Promise<ApiResponse<GameRoom>>;
  readonly listGames: () => Promise<ApiResponse<GameRoom[]>>;
  readonly listMyGames: () => Promise<ApiResponse<GameRoom[]>>;
  readonly joinGame: (roomId: string) => Promise<ApiResponse<GameRoom>>;
  readonly joinObserver: (roomId: string) => Promise<ApiResponse<GameRoom>>;
  readonly cancelGame: (
    roomId: string,
  ) => Promise<ApiResponse<{ message: string }>>;
  readonly leaveGame: (
    roomId: string,
  ) => Promise<ApiResponse<{ message: string }>>;
  readonly startGame: (roomId: string) => Promise<ApiResponse<GameRoom>>;
  readonly submitEvent: (
    roomId: string,
    eventType: string,
    payload?: Record<string, unknown>,
  ) => Promise<ApiResponse<GameEvent>>;
  readonly getGameEvents: (
    roomId: string,
    after?: number,
  ) => Promise<ApiResponse<GameEvent[]>>;
  /**
   * ADR-04: fetch per-recipient projected ViewerStates (hidden-information
   * games). Server returns only the caller's own rows. `state` is an
   * already-filtered ViewerState — never a raw GameState.
   */
  readonly getViewerStates: (
    roomId: string,
    after?: number,
  ) => Promise<ApiResponse<ViewerStateRow[]>>;
  readonly getMyStats: () => Promise<ApiResponse<PlayerStats>>;
}

export interface ViewerStateRow {
  readonly id: string;
  readonly roomId: string;
  readonly recipientUserId: string;
  readonly sequenceNumber: number;
  readonly state: Record<string, unknown>;
  readonly createdAt: string;
}

/**
 * Creates a game API client pre-bound to a specific game type.
 */
export function createGameApi(gameType: string, _mode?: GameApiMode): GameApi {
  return createGamePlatformGameApi(gameType);
}

function createGamePlatformGameApi(gameType: string): GameApi {
  return {
    getRoom: async (roomId) =>
      mapResponse(await platformApi.getRoom(roomId), mapRoom),
    createShareLink: async (roomId) => {
      const clientBaseUrl =
        typeof window === "undefined" ? undefined : window.location.origin;
      return mapResponse(
        await platformApi.createShareLink(roomId, clientBaseUrl),
        (shareLink) => shareLink,
      );
    },
    createObserverShareLink: async (roomId) => {
      const clientBaseUrl =
        typeof window === "undefined" ? undefined : window.location.origin;
      return mapResponse(
        await platformApi.createObserverShareLink(roomId, clientBaseUrl),
        (shareLink) => shareLink,
      );
    },
    resolveShareLink: async (gt, roomNumber, secret) =>
      mapResponse(
        await platformApi.resolveShareLink(gt, roomNumber, secret),
        (shareLink) => shareLink,
      ),
    joinByShareLink: async (gt, roomNumber, secret) =>
      mapResponse(
        await platformApi.joinByShareLink(gt, roomNumber, secret),
        mapRoom,
      ),
    joinObserverByShareLink: async (gt, roomNumber, secret) =>
      mapResponse(
        await platformApi.joinByShareLink(gt, roomNumber, secret),
        mapRoom,
      ),
    createGame: async (config = {}) => {
      const { gameConfig, options } = splitRoomOptions(config);
      return mapResponse(
        await platformApi.createRoom({
          gameType,
          config: gameConfig,
          ...options,
        }),
        mapRoom,
      );
    },
    listGames: async () =>
      mapResponse(await platformApi.listRooms(gameType), (rooms) =>
        rooms.map(mapRoom),
      ),
    listMyGames: async () =>
      mapResponse(await platformApi.listMyRooms(gameType), (rooms) =>
        rooms.map(mapRoom),
      ),
    joinGame: async (roomId) =>
      mapResponse(await platformApi.joinRoom(roomId), mapRoom),
    joinObserver: async (roomId) =>
      mapResponse(await platformApi.joinObserver(roomId), mapRoom),
    cancelGame: async (roomId) =>
      mapResponse(await platformApi.cancelRoom(roomId), (message) => message),
    leaveGame: async (roomId) =>
      mapResponse(await platformApi.cancelRoom(roomId), (message) => message),
    startGame: async (roomId) =>
      mapResponse(await platformApi.startRoom(roomId), mapRoom),
    submitEvent: (roomId, eventType, payload = {}) =>
      submitPlatformEvent(platformApi, roomId, gameType, eventType, payload),
    getGameEvents: async (roomId, after) =>
      mapResponse(await platformApi.getEvents(roomId, after), (events) =>
        events.map(mapEvent),
      ),
    getViewerStates: async (roomId, after) =>
      mapResponse(await platformApi.getViewerStates(roomId, after), (states) =>
        states.map((state) => ({
          id: state.id,
          roomId: state.roomId,
          recipientUserId: state.recipientUserId,
          sequenceNumber: state.sequenceNumber,
          state: { ...state.state },
          createdAt: state.createdAt,
        })),
      ),
    getMyStats: () => getMyStats(gameType),
  };
}

/**
 * Replay shares are game-agnostic platform resources (not bound to a single
 * game type), so they live as standalone functions rather than on GameApi.
 */
export function createReplayShare(input: {
  readonly gameType: string;
  readonly events: readonly unknown[];
}): Promise<ApiResponse<ReplayShare>> {
  return platformApi.createReplayShare(input);
}

export function resolveReplayShare(
  code: string,
): Promise<ApiResponse<ReplayShare>> {
  return platformApi.resolveReplayShare(code);
}

function getMyStats(gameType: string): Promise<ApiResponse<PlayerStats>> {
  const query = new URLSearchParams({ gameType });
  return authFetch<PlayerStats>(`/api/v2/players/me/stats?${query}`, {
    method: "GET",
  });
}

function splitRoomOptions(config: Record<string, unknown>): {
  readonly gameConfig: Record<string, unknown>;
  readonly options: {
    readonly minPlayers?: number;
    readonly maxPlayers?: number;
    readonly observerMode?: boolean;
    readonly observerSeatCount?: number;
  };
} {
  const {
    minPlayers,
    maxPlayers,
    observerMode,
    observerSeatCount,
    ...gameConfig
  } = config as {
    minPlayers?: unknown;
    maxPlayers?: unknown;
    observerMode?: unknown;
    observerSeatCount?: unknown;
  } & Record<string, unknown>;
  const options: {
    minPlayers?: number;
    maxPlayers?: number;
    observerMode?: boolean;
    observerSeatCount?: number;
  } = {};
  if (typeof minPlayers === "number") options.minPlayers = minPlayers;
  if (typeof maxPlayers === "number") options.maxPlayers = maxPlayers;
  if (typeof observerMode === "boolean") options.observerMode = observerMode;
  if (typeof observerSeatCount === "number") {
    options.observerSeatCount = observerSeatCount;
  }
  return { gameConfig, options };
}

function mapResponse<TInput, TOutput>(
  response: ApiResponse<TInput>,
  mapper: (value: TInput) => TOutput,
): ApiResponse<TOutput> {
  if (!response.success || response.data === undefined) {
    return {
      success: false,
      error: response.error,
      errorCode: response.errorCode,
      details: response.details,
    };
  }
  return { success: true, data: mapper(response.data) };
}

function mapRoom(room: GamePlatformRoom): GameRoom {
  return {
    id: room.id,
    gameType: room.gameType,
    status: room.status,
    hostUserId: room.hostUserId,
    guestUserId: room.guestUserId,
    participantUserIds: copyArray(room.participantUserIds),
    participants: room.participants?.map((participant) => ({
      userId: participant.userId,
      displayName: participant.displayName,
      isGuest: participant.isGuest,
    })),
    currentPlayerCount: room.currentPlayerCount,
    observerMode: room.observerMode,
    observerSeatCount: room.observerSeatCount,
    observerUserIds: copyArray(room.observerUserIds),
    observerParticipants: room.observerParticipants?.map((participant) => ({
      userId: participant.userId,
      displayName: participant.displayName,
      isGuest: participant.isGuest,
    })),
    currentObserverCount: room.currentObserverCount,
    minPlayers: room.minPlayers,
    maxPlayers: room.maxPlayers,
    config: { ...room.config },
    createdAt: room.createdAt,
    startedAt: room.startedAt,
    completedAt: room.completedAt,
  };
}

function mapEvent(event: GamePlatformEvent): GameEvent {
  return {
    id: event.id,
    gameId: event.gameId,
    eventType: event.eventType,
    sequenceNumber: event.sequenceNumber,
    userId: event.userId,
    payload: { ...event.payload },
    createdAt: event.createdAt,
  };
}

function copyArray<T>(value: readonly T[] | undefined): T[] {
  return Array.isArray(value) ? [...value] : [];
}
